import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { decryptAESID } from "../../../utils/Encryption";
import {useGetAdditionalFieldValueBySelfEvalRequirementIdForAdminMutation,useGetFileListBySelfEvalRequirementIdForAdminMutation,useLazyGetSelfEvaluationRequirementBySelfEvaluationIdForAdminQuery,useSaveSelfEvalutionFormInfomationByPeerReviewerMutation,} from "../../../app/services/selfEvalutionReviewService/selfEvalutionReviewServiceApi";
import ToastService from "../../../services/toastService/ToastService";
import { usePeerReviewerServiceContext } from "../ServiceContext";
export const buildFieldKey = (rawFieldId, brandId) =>brandId != null ? `${rawFieldId}_${brandId}` : `${rawFieldId}`;

export function useSectionRequirements(section, useListQuery, useFieldsQuery) {
    const { id } = useParams();
    const { state, dispatch } = usePeerReviewerServiceContext();
    const selfEvaluationId = state?.sections?.[section]?.selfEvaluationId;
    const [getList, { isLoading: listLoading, isSuccess: listSuccess, data: listData }] = useListQuery();
    const [getFields, { isLoading: fieldsLoading, isSuccess: fieldsSuccess, data: fieldsData }] = useFieldsQuery();
    const [getFileList, { isLoading: fileLoading, isSuccess: fileSuccess, data: fileData }] = useGetFileListBySelfEvalRequirementIdForAdminMutation();
    const [getAdditionalFieldValue, { isLoading: valuesLoading, isSuccess: valuesSuccess, data: valuesData }] = useGetAdditionalFieldValueBySelfEvalRequirementIdForAdminMutation();
    const [getSelfEvalRequirement, { isLoading: reqLoading, isSuccess: reqSuccess, data: reqData }] = useLazyGetSelfEvaluationRequirementBySelfEvaluationIdForAdminQuery();
    const [saveComment, { isLoading: saveLoading, isSuccess: saveSuccess, data: saveData }] = useSaveSelfEvalutionFormInfomationByPeerReviewerMutation();
    const applicationId = useMemo(() => decryptAESID(id?.toString(), false), [id]);

    useEffect(() => {
        if (applicationId) {
            getList(applicationId);
            getFields(applicationId);
        }
    }, [applicationId]);

    useEffect(() => {
        if (selfEvaluationId > 0) {
            getSelfEvalRequirement(selfEvaluationId);
        }
    }, [selfEvaluationId]);

    useEffect(() => {
        if (!listLoading && listSuccess && listData) {
            dispatch({ type: "SET_REQ_LIST", section, reqList: listData });
        }
    }, [listLoading, listSuccess, listData]);

    useEffect(() => {
        if (!fieldsLoading && fieldsSuccess && fieldsData) {
            const data = fieldsData.map((item) => ({ ...item, isRequired: item?.ismandatory }));
            dispatch({ type: "SET_FIELD_LIST", section, fieldList: data });
        }
    }, [fieldsLoading, fieldsSuccess, fieldsData]);

    useEffect(() => {
        if (!reqLoading && reqSuccess && reqData) {
            const ids = reqData.map((item) => item.selfEvalRequirementId);
            if (ids.length > 0 && selfEvaluationId > 0) {
                getFileList({ selfEvaluationId, requirementIds: ids });
                getAdditionalFieldValue({ selfEvaluationId, requirementIds: ids });
            }
            reqData.forEach((item) => {
                dispatch({
                    type: "SET_REQ_COMMENT",
                    section: item?.section,
                    reqId: item?.requirementId,
                    comment: item?.comment,
                    selfEvalRequirementId: item?.selfEvalRequirementId,
                    caseOfficerComments: item?.clarificationComments,
                    status: item.statusName,
                    peerReviewerComments: item?.peerReviewerComment,
                });
            });
        }
    }, [reqLoading, reqSuccess, reqData]);

    useEffect(() => {
        if (!fileLoading && fileSuccess && fileData) {
            fileData.forEach((item) => {
                const existingFiles =
                    state.sections?.[section]?.requirements?.[item?.requirementId]?.files || [];
                const alreadyExists = existingFiles.some(
                    (f) => f.name === item?.selfEvaluationFileName && f.url === item?.selfEvaluationFilePath
                );
                if (!alreadyExists) {
                    dispatch({
                        type: "ADD_FILE",
                        section,
                        reqId: item?.requirementId,
                        selfEvalRequirementId: item?.selfEvalRequirementId,
                        file: {
                            id: item?.fileId,
                            name: item?.selfEvaluationFileName,
                            url: item?.selfEvaluationFilePath,
                        },
                    });
                }
            });
        }
    }, [fileLoading, fileSuccess, fileData]);

    useEffect(() => {
        if (!valuesLoading && valuesSuccess && valuesData) {
            valuesData.forEach((item) => {
                const fieldId = buildFieldKey(item?.additionalFieldId, item?.productDetailsId);
                dispatch({
                    type: "SET_FIELD",
                    section,
                    fieldId,
                    value: item?.additionalFieldValue,
                    reqId: item?.requirementId,
                    selfEvalRequirementId: item?.selfEvalRequirementId,
                    id: item.id,
                    additionalFieldId: item?.additionalFieldId,
                    brandItemId: item?.productDetailsId || null,
                });
                dispatch({
                    type: "SET_FIELD_COMMENT",
                    section,
                    fieldId,
                    comment: item?.comments,
                    reqId: item?.requirementId,
                    selfEvalRequirementId: item?.selfEvalRequirementId,
                    id: item.id,
                    additionalFieldId: item?.additionalFieldId,
                    brandItemId: item?.productDetailsId || null,
                });
            });
        }
    }, [valuesLoading, valuesSuccess, valuesData]);

    useEffect(() => {
        if (!saveLoading && saveSuccess && saveData) {
            ToastService.success("Peer Reviewer Comment Save");
        }
    }, [saveLoading, saveSuccess, saveData]);

    const record = state?.sections?.[section]?.reqList || [];
    const fieldList = state?.sections?.[section]?.fieldList || [];

    const handlePeerReviewerComment = (reqId, value) => {
        const existing = state.sections?.[section]?.requirements?.[reqId];
        dispatch({
            type: "SET_REQ_COMMENT",
            section,
            reqId,
            comment: existing?.comment,
            selfEvalRequirementId: existing?.selfEvalRequirementId,
            caseOfficerComments: existing?.caseOfficerComments,
            status: existing?.status,
            peerReviewerComments: value,
        });
    };

    const handleClarificationSend = (reqId) => {
        const existing = state.sections?.[section]?.requirements?.[reqId];
        saveComment({
            SelfEvaluationRequirementId: existing?.selfEvalRequirementId,
            PeerReviewerComment: existing?.peerReviewerComments,
        });
    };

    const isFieldInvalid = (fieldKey, isRequired) => {
        if (!isRequired) return false;
        const fieldState = state.sections?.[section]?.fields?.[fieldKey];
        const isEmpty = !fieldState?.value && fieldState?.value !== false && fieldState?.value !== 0;
        const shouldShow = fieldState?.touched || state.submitAttempted;
        return isEmpty && shouldShow;
    };

    return {
        state,
        dispatch,
        record,
        fieldList,
        handlePeerReviewerComment,
        handleClarificationSend,
        isFieldInvalid,
        saveLoading,
    };
}